import { lazy } from 'react';

// Lazy load pages for code splitting
const Landing = lazy(() => import('../pages/Landing'));
const Login = lazy(() => import('../pages/Login'));
const Onboarding = lazy(() => import('../pages/Onboarding'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Products = lazy(() => import('../pages/Products'));
const NewProduct = lazy(() => import('../pages/Products/NewProduct'));
const UpdatePrice = lazy(() => import('../pages/Products/UpdatePrice'));
const ProductVariations = lazy(() => import('../pages/Products/ProductVariations'));
const BulkImport = lazy(() => import('../pages/Products/BulkImport'));
const BulkStockImport = lazy(() => import('../pages/Products/BulkStockImport'));
const Recipes = lazy(() => import('../pages/Recipes'));
const KDS = lazy(() => import('../pages/KDS'));
const PriceGroups = lazy(() => import('../pages/Products/PriceGroups'));
const PriceList = lazy(() => import('../pages/Products/PriceList'));
const Units = lazy(() => import('../pages/Products/Units'));
const Categories = lazy(() => import('../pages/Products/Categories'));
const Brands = lazy(() => import('../pages/Products/Brands'));
const Warranties = lazy(() => import('../pages/Products/Warranties'));
const Sales = lazy(() => import('../pages/Sales'));
// webpackPrefetch: the browser downloads the POS chunk during idle time after the
// initial page loads, so navigating to /sales/pos (the most-used screen) finds its
// JS already cached instead of fetching it on click. Prefetch is low-priority and
// won't slow the current page, even on slow connections.
const NewSale = lazy(() => import(/* webpackPrefetch: true */ '../pages/Sales/NewSale'));
const SalesHistory = lazy(() => import('../pages/Sales/SalesHistory'));
const AllOrders = lazy(() => import('../pages/Sales/AllOrders'));
const SalesPersonStatement = lazy(() => import('../pages/Sales/SalesPersonStatement'));
const SalespersonWallets = lazy(() => import('../pages/Sales/SalespersonWallets'));
const SalesReturns = lazy(() => import('../pages/Sales/SalesReturns'));
const Cashier = lazy(() => import('../pages/Cashier'));
const PaymentMethodReport = lazy(() => import('../pages/Cashier/PaymentMethodReport'));
const MpesaNotifications = lazy(() => import('../pages/Cashier/MpesaNotifications'));
const SubmittedPayments = lazy(() => import('../pages/Cashier/SubmittedPayments'));
const TotalSalesPerPerson = lazy(() => import('../pages/Cashier/TotalSalesPerPerson'));
const ItemSalesReport = lazy(() => import('../pages/Cashier/ItemSalesReport'));
const SalesDetailReport = lazy(() => import('../pages/Cashier/SalesDetailReport'));
const TotalSalesPerWarehouse = lazy(() => import('../pages/Cashier/TotalSalesPerWarehouse'));
const SalesInvoiceDetails = lazy(() => import('../pages/Sales/SalesInvoiceDetails'));
const POSInvoiceDetails = lazy(() => import('../pages/Sales/POSInvoiceDetails'));
const NewSalesInvoice = lazy(() => import('../pages/Sales/NewSalesInvoice'));
const POSOpeningEntries = lazy(() => import('../pages/Sales/POSOpeningEntries'));
const POSOpeningEntryDetails = lazy(() => import('../pages/Sales/POSOpeningEntryDetails'));
const PrintInvoice = lazy(() => import('../pages/Sales/PrintInvoice'));
const Warehouses = lazy(() => import('../pages/Warehouses'));
const WarehouseDetails = lazy(() => import('../pages/Inventory/WarehouseDetails'));
// const WarehouseForm = lazy(() => import('../pages/Inventory/WarehouseForm'));
const WarehouseStaff = lazy(() => import('../pages/Inventory/WarehouseStaff'));
const MaterialReceipt = lazy(() => import('../pages/Inventory/MaterialReceipt'));
const MaterialIssue = lazy(() => import('../pages/Inventory/MaterialIssue'));
const MaterialTransfer = lazy(() => import('../pages/Inventory/MaterialTransfer'));
const StockEntry = lazy(() => import('../pages/Inventory/StockEntry'));
const StockReconciliation = lazy(() => import('../pages/Inventory/StockReconciliation'));
const InventoryLayout = lazy(() => import('../pages/Inventory/InventoryLayout'));
const InventoryOverview = lazy(() => import('../pages/Inventory/InventoryOverview'));
const StockSummary = lazy(() => import('../pages/Inventory/StockSummary'));
const LowStockAlert = lazy(() => import('../pages/Inventory/LowStockAlert'));
const StockLedger = lazy(() => import('../pages/Inventory/StockLedger'));
const InventoryItemDetails = lazy(() => import('../pages/Inventory/InventoryItemDetails'));
const StockEntryList = lazy(() => import('../pages/Inventory/StockEntryList'));
const StockEntryDetails = lazy(() => import('../pages/Inventory/StockEntryDetails'));
const MaterialReceiptsList = lazy(() => import('../pages/Inventory/MaterialReceiptsList'));
const MaterialIssuesList = lazy(() => import('../pages/Inventory/MaterialIssuesList'));
const CounterStockCheck = lazy(() => import('../pages/Inventory/CounterStockCheck'));
const StockSnapshot = lazy(() => import('../pages/Inventory/StockSnapshot'));
const MaterialTransfersList = lazy(() => import('../pages/Inventory/MaterialTransfersList'));
// Multi-Level Stock Reconciliation
const MultiLevelReconciliation = lazy(() => import('../pages/Inventory/MultiLevelReconciliation'));
const MultiLevelReconciliationDetails = lazy(() => import('../pages/Inventory/MultiLevelReconciliationDetails'));
const StockTake = lazy(() => import('../pages/Inventory/StockTake'));
const CreateMultiLevelReconciliation = lazy(() => import('../pages/Inventory/CreateMultiLevelReconciliation'));
const Customers = lazy(() => import('../pages/Customers'));
const CustomerCredit = lazy(() => import('../pages/Customers/Credit'));
const CustomerReport = lazy(() => import('../pages/Customers/CustomerReport'));
// CRM module
const CRM = lazy(() => import('../pages/CRM'));
const CRMCustomerDetail = lazy(() => import('../pages/CRM/CustomerDetail'));
const CRMPromotions = lazy(() => import('../pages/CRM/Promotions'));
const CRMBroadcasts = lazy(() => import('../pages/CRM/Broadcasts'));
const Suppliers = lazy(() => import('../pages/Suppliers'));
const SupplierDetails = lazy(() => import('../pages/Suppliers/SupplierDetails'));
const SupplierReport = lazy(() => import('../pages/Suppliers/SupplierReport'));
const SupplierGroups = lazy(() => import('../pages/Suppliers/SupplierGroups'));
const Purchases = lazy(() => import('../pages/Purchases'));
const NewPurchase = lazy(() => import('../pages/Purchases/NewPurchase'));
const PurchaseReturns = lazy(() => import('../pages/Purchases/PurchaseReturns'));
const PurchaseReturnDetails = lazy(() => import('../pages/Purchases/PurchaseReturnDetails'));
const PurchaseDetails = lazy(() => import('../pages/Purchases/PurchaseDetails'));
const PurchaseReceiptsList = lazy(() => import('../pages/Purchases/PurchaseReceiptsList'));
const NewPurchaseReceipt = lazy(() => import('../pages/Purchases/NewPurchaseReceipt'));
const PurchaseReceiptDetails = lazy(() => import('../pages/Purchases/PurchaseReceiptDetails'));
// Purchase Order Flow Forms (based on PURCHASE_API_DOCUMENTATION.md)
const CreatePurchaseOrder = lazy(() => import('../pages/Purchases/CreatePurchaseOrder'));
const SubmitPurchaseOrder = lazy(() => import('../pages/Purchases/SubmitPurchaseOrder'));
const GRNList = lazy(() => import('../pages/Purchases/GRNList'));
const GRNDetails = lazy(() => import('../pages/Purchases/GRNDetails'));
// Purchase Invoice pages (based on PURCHASE_INVOICE_API_DOCUMENTATION.md)
const PurchaseInvoicesList = lazy(() => import('../pages/Purchases/PurchaseInvoicesList'));
const PurchaseInvoiceDetails = lazy(() => import('../pages/Purchases/PurchaseInvoiceDetails'));
const Expenses = lazy(() => import('../pages/Purchases/Expenses'));
const Staff = lazy(() => import('../pages/Staff'));
const Reports = lazy(() => import('../pages/Reports'));
const SalesAnalyticsCharts = lazy(() => import('../pages/Reports/SalesAnalytics/SalesAnalyticsCharts'));
const SalesAnalyticsTable = lazy(() => import('../pages/Reports/SalesAnalytics/SalesAnalyticsTable'));
const ProductSalesAnalytics = lazy(() => import('../pages/Reports/SalesAnalytics/ProductSalesAnalytics'));
const SalesPerformance = lazy(() => import('../pages/Reports/SalesPerformance'));
const WaiterRevenue = lazy(() => import('../pages/Reports/WaiterRevenue'));
const AccountsReports = lazy(() => import('../pages/Reports/Accounts'));
const ZReport = lazy(() => import('../pages/Reports/Accounts/ZReport'));
const ProfitAndLoss = lazy(() => import('../pages/Reports/Accounts/ProfitAndLoss'));
const CustomerStatement = lazy(() => import('../pages/Reports/Accounts/CustomerStatement'));
const CustomerStatementDetail = lazy(() => import('../pages/Reports/Accounts/CustomerStatementDetail'));
const InventorySummary = lazy(() => import('../pages/Reports/InventorySummary'));
const InventoryValuation = lazy(() => import('../pages/Reports/InventoryValuation'));
const ValueByCategory = lazy(() => import('../pages/Reports/InventoryValuation/ValueByCategory'));
const InventoryValue = lazy(() => import('../pages/Reports/InventoryValuation/InventoryValue'));
const CostMethodComparison = lazy(() => import('../pages/Reports/InventoryValuation/CostMethodComparison'));
const ValueTrends = lazy(() => import('../pages/Reports/InventoryValuation/ValueTrends'));
const AgingStock = lazy(() => import('../pages/Reports/AgingStock'));
const StockAging = lazy(() => import('../pages/Reports/AgingStock/StockAging'));
const ObsolescenceRisk = lazy(() => import('../pages/Reports/AgingStock/ObsolescenceRisk'));
const AgingRecommendations = lazy(() => import('../pages/Reports/AgingStock/AgingRecommendations'));
const PerformanceMetrics = lazy(() => import('../pages/Reports/PerformanceMetrics'));
const AccuracyReport = lazy(() => import('../pages/Reports/PerformanceMetrics/AccuracyReport'));
const VarianceReport = lazy(() => import('../pages/Reports/PerformanceMetrics/VarianceReport'));
const AdjustmentTrends = lazy(() => import('../pages/Reports/PerformanceMetrics/AdjustmentTrends'));
const TransferEfficiency = lazy(() => import('../pages/Reports/PerformanceMetrics/TransferEfficiency'));
const Settings = lazy(() => import('../pages/Settings'));
const POSProfileSettings = lazy(() => import('../pages/Settings/POSProfileSettings'));
const ETIMSSettings = lazy(() => import('../pages/Settings/ETIMSSettings'));
const BankAccounts = lazy(() => import('../pages/Settings/BankAccounts'));
const AccountProvisioning = lazy(() => import('../pages/Settings/AccountProvisioning'));
const PaymentMethods = lazy(() => import('../pages/Settings/PaymentMethods'));
const InventoryDiscounts = lazy(() => import('../pages/Settings/InventoryDiscounts'));
const DiscountRuleForm = lazy(() => import('../pages/Settings/DiscountRuleForm'));
const LoyaltyPrograms = lazy(() => import('../pages/Settings/LoyaltyPrograms'));
const ModuleVisibility = lazy(() => import('../pages/Settings/ModuleVisibility'));
const TillNumbers = lazy(() => import('../pages/Settings/TillNumbers'));
const DatabaseBackup = lazy(() => import('../pages/Settings/DatabaseBackup'));
const BusinessSettings = lazy(() => import('../pages/Settings/BusinessSettings'));
// Roles
const Roles = lazy(() => import('../pages/Roles'));
const RoleDetails = lazy(() => import('../pages/Roles/RoleDetails'));
const RolePermissions = lazy(() => import('../pages/Roles/RolePermissions'));
const ModulePermissionsPage = lazy(() => import('../pages/Roles/ModulePermissionsPage'));
const RoleForm = lazy(() => import('../pages/Roles/RoleForm'));
const IndustryProductSetup = lazy(() => import('../pages/IndustryProductSetup'));
const StockAdjustmentLog = lazy(() => import('../pages/Inventory/StockAdjustmentLog'));
// Stock Transfers
const StockTransfers = lazy(() => import('../pages/StockTransfers'));
const StockTransfersList = lazy(() => import('../pages/StockTransfers/StockTransfersList'));
const StockTransferDetails = lazy(() => import('../pages/StockTransfers/StockTransferDetails'));
const CreateMaterialRequest = lazy(() => import('../pages/StockTransfers/CreateMaterialRequest'));
const ApproveTransferRequest = lazy(() => import('../pages/StockTransfers/ApproveTransferRequest'));
const DispatchStock = lazy(() => import('../pages/StockTransfers/DispatchStock'));
const ReceiveStock = lazy(() => import('../pages/StockTransfers/ReceiveStock'));
const NotFound = lazy(() => import('../pages/NotFound'));
const PermissionDenied = lazy(() => import('../pages/PermissionDenied'));
const Attendance = lazy(() => import('../pages/Attendance'));
const StaffPerformance = lazy(() => import('../pages/StaffPerformance'));
const ShiftManagement = lazy(() => import('../pages/ShiftManagement'));
const Marketing = lazy(() => import('../pages/Marketing'));
const ServiceTickets = lazy(() => import('../pages/ServiceTickets'));
const NewTicket = lazy(() => import('../pages/ServiceTickets/NewTicket'));
const TicketDetails = lazy(() => import('../pages/ServiceTickets/TicketDetails'));

// Production category (make-to-order kitchen)
const Production = lazy(() => import('../pages/Production'));
const KitchenFoods = lazy(() => import('../pages/Production/KitchenFoods'));
const ProductionFactors = lazy(() => import('../pages/Production/Factors'));
const ProductionMonitor = lazy(() => import('../pages/Production/Monitor'));
const ProductionUom = lazy(() => import('../pages/Production/Uom'));
const KitchenItemSales = lazy(() => import('../pages/Production/KitchenItemSales'));
const KitchenStaffSales = lazy(() => import('../pages/Production/KitchenStaffSales'));
const PurchaseRequisition = lazy(() => import('../pages/Production/PurchaseRequisition'));
const RestaurantGRN = lazy(() => import('../pages/Production/RestaurantGRN'));
const RestaurantPO = lazy(() => import('../pages/Purchases/RestaurantPO'));

// Public routes (without Layout)
export const publicRoutes = [
  {
    path: '/',
    element: Landing,
    label: 'Home',
  },
  {
    path: '/login',
    element: Login,
    label: 'Login',
  },
];

// Protected routes without Layout (e.g., onboarding, print pages)
export const protectedRoutesWithoutLayout = [
  {
    path: '/onboarding',
    element: Onboarding,
    label: 'Onboarding',
  },
  {
    path: '/print/invoice/:id',
    element: PrintInvoice,
    label: 'Print Invoice',
  },
  {
    path: '/print/pos-invoice/:id',
    element: PrintInvoice,
    label: 'Print POS Invoice',
  },
];

// Protected routes (with Layout)
export const protectedRoutes = [
  {
    path: '/dashboard',
    element: Dashboard,
    label: 'Dashboard',
    icon: 'dashboard',
  },
  {
    path: '/kds',
    element: KDS,
    label: 'Kitchen Dashboard',
    icon: 'dashboard',
  },
  {
    path: '/production',
    element: Production,
    label: 'Production',
    icon: 'beaker',
    pageChildren: [
      { path: '/production/kitchen-foods', label: 'Menu', icon: 'restaurant' },
      { path: '/production/factors', label: 'Ingredients', icon: 'inventory' },
      { path: '/production/monitor', label: 'Production Monitor', icon: 'dashboard' },
      { path: '/production/uom', label: 'Units of Measure', icon: 'list' },
      { path: '/production/item-sales', label: 'Item Sales', icon: 'bar_chart' },
      { path: '/production/staff-sales', label: 'Sales by Person', icon: 'people' },
      { path: '/production/purchase-requisition', label: 'Purchase Requisition', icon: 'shoppingcart' },
      { path: '/production/restaurant-grn', label: 'Restaurant GRN', icon: 'receipt' },
    ],
    children: [
      {
        path: '/production/kitchen-foods',
        element: KitchenFoods,
        label: 'Menu',
        icon: 'restaurant',
      },
      {
        path: '/production/factors',
        element: ProductionFactors,
        label: 'Ingredients',
        icon: 'inventory',
      },
      {
        path: '/production/monitor',
        element: ProductionMonitor,
        label: 'Production Monitor',
        icon: 'dashboard',
      },
      {
        path: '/production/uom',
        element: ProductionUom,
        label: 'Units of Measure',
        icon: 'list',
      },
      {
        path: '/production/item-sales',
        element: KitchenItemSales,
        label: 'Item Sales',
        icon: 'bar_chart',
      },
      {
        path: '/production/staff-sales',
        element: KitchenStaffSales,
        label: 'Sales by Person',
        icon: 'people',
      },
      {
        path: '/production/purchase-requisition',
        element: PurchaseRequisition,
        label: 'Purchase Requisition',
        icon: 'shoppingcart',
      },
      {
        path: '/production/restaurant-grn',
        element: RestaurantGRN,
        label: 'Restaurant GRN',
        icon: 'receipt',
      },
    ],
  },
  {
    path: '/industry/:industryCode/products',
    element: IndustryProductSetup,
    label: 'Industry Products',
    icon: 'inventory',
    hideFromMenu: true,
  },
  {
    path: '/products',
    element: Products,
    label: 'Products',
    icon: 'inventory',
    requiredDocTypes: [{ doctype: 'Item', action: 'read' }],
    // Page-specific child navigation
    pageChildren: [
      { path: '/products', label: 'All Products', icon: 'inventory' },
      { path: '/products/categories', label: 'Categories', icon: 'folder' },
      { path: '/products/brands', label: 'Brands', icon: 'tag' },
      { path: '/products/units', label: 'UOMs', icon: 'list' },
      { path: '/products/warranties', label: 'Warranties', icon: 'verified' },
      { path: '/products/price-groups', label: 'Price Lists', icon: 'money' },
      { path: '/products/price-list', label: 'Print Price List', icon: 'money' },
      { path: '/products/recipes', label: 'Recipes (BOM)', icon: 'restaurant' },
    ],
    children: [
      {
        path: '/products',
        element: Products,
        label: 'All Products',
        icon: 'inventory',
      },
      {
        path: '/products/new',
        element: NewProduct,
        label: 'New Product',
        icon: 'add',
        hideFromMenu: true
      },
      {
        path: '/products/update-price',
        element: UpdatePrice,
        label: 'Update Price',
        icon: 'money',
        hideFromMenu: true
      },
      {
        path: '/products/variations',
        element: ProductVariations,
        label: 'Product Variations',
        icon: 'folder',
        hideFromMenu: true
      },
      {
        path: '/products/bulk-import',
        element: BulkImport,
        label: 'Bulk Products Import',
        icon: 'upload',
        hideFromMenu: true
      },
      {
        path: '/products/bulk-stock-import',
        element: BulkStockImport,
        label: 'Bulk Opening Stock Import',
        icon: 'inventory',
        hideFromMenu: true
      },
      {
        path: '/products/price-groups',
        element: PriceGroups,
        label: 'Selling Price Groups',
        icon: 'money',
      },
      {
        path: '/products/price-list',
        element: PriceList,
        label: 'Print Price List',
        icon: 'money',
      },
      {
        path: '/products/units',
        element: Units,
        label: 'Units Management',
        icon: 'list',
      },
      {
        path: '/products/categories',
        element: Categories,
        label: 'Categories',
        icon: 'folder',
      },
      {
        path: '/products/brands',
        element: Brands,
        label: 'Brands',
        icon: 'tag',
      },
      {
        path: '/products/warranties',
        element: Warranties,
        label: 'Warranties',
        icon: 'verified',
      },
      {
        path: '/products/recipes',
        element: Recipes,
        label: 'Recipes (BOM)',
        icon: 'restaurant',
        hideFromMenu: true
      },
    ],
  },
  {
    path: '/sales',
    element: Sales,
    label: 'Sales',
    icon: 'sales',
    requiredDocTypes: [{ doctype: 'Sales Invoice', action: 'read/create' }],
    // Page-specific child navigation
    pageChildren: [
      { path: '/sales/pos', label: 'Point of Sale', icon: 'pointofsale' },
      { path: '/sales/orders', label: 'All Orders', icon: 'sales' },
      { path: '/sales/returns', label: 'Product Returns', icon: 'assignmentreturn' },
      { path: '/sales/wallets', label: 'Salesperson Wallets', icon: 'cashier' },
      { path: '/sales/history', label: 'Sales History', icon: 'history' },
      { path: '/sales/invoice/new', label: 'New Invoice', icon: 'add' },
      { path: '/sales/pos-opening-entries', label: 'Opening Entries', icon: 'history' },
    ],
    children: [
      {
        path: '/sales/pos',
        element: NewSale,
        label: 'Point of Sale',
        icon: 'sales',
      },
      {
        path: '/sales/orders',
        element: AllOrders,
        label: 'All Orders',
        icon: 'sales',
      },
      {
        path: '/sales/returns',
        element: SalesReturns,
        label: 'Product Returns',
        icon: 'assignmentreturn',
      },
      {
        path: '/sales/allocations/statement',
        element: SalesPersonStatement,
        label: 'Salesperson Statement',
        icon: 'receipt',
        hideFromMenu: true,
      },
      {
        path: '/sales/wallets',
        element: SalespersonWallets,
        label: 'Salesperson Wallets',
        icon: 'cashier',
      },
      {
        path: '/sales/history',
        element: SalesHistory,
        label: 'Sales History',
        icon: 'history',
      },
      {
        path: '/sales/invoice/new',
        element: NewSalesInvoice,
        label: 'New Sales Invoice',
        icon: 'add',
        hideFromMenu: true
      },
      {
        path: '/sales/pos-opening-entries',
        element: POSOpeningEntries,
        label: 'POS Opening Entries',
        icon: 'history',
      },
      // Dynamic routes (not in sidebar, accessed via links)
      {
        path: '/sales/invoice/:id',
        element: SalesInvoiceDetails,
        label: 'Sales Invoice Details',
        hideFromMenu: true,
      },
      {
        path: '/sales/pos-opening-entries/:name',
        element: POSOpeningEntryDetails,
        label: 'POS Opening Entry Details',
        hideFromMenu: true,
      },
      {
        path: '/sales/pos-invoice/:id',
        element: POSInvoiceDetails,
        label: 'POS Invoice Details',
        hideFromMenu: true,
      },
      {
        path: '/sales/invoice/:id/edit',
        element: NewSalesInvoice,
        label: 'Edit Sales Invoice',
        hideFromMenu: true,
      },
    ],
  },
  {
    path: '/warehouses',
    element: Warehouses,
    label: 'Stores',
    icon: 'stores',
    requiredDocTypes: [{ doctype: 'Warehouse', action: 'read' }],
    // Page-specific child navigation
    pageChildren: [
      { path: '/warehouses', label: 'All Stores', icon: 'stores' },
      // { path: '/warehouses/new', label: 'New Store', icon: 'Add' },
    ],
    children: [
      {
        path: '/warehouses',
        element: Warehouses,
        label: 'All Stores',
        icon: 'stores',
      },
      // Removed New Store child as it's now a dialog
      // Dynamic routes (not in sidebar, accessed via links)
      {
        path: '/warehouses/:id',
        element: WarehouseDetails,
        label: 'Store Details',
        hideFromMenu: true,
      },
      {
        path: '/warehouses/:id/staff',
        element: WarehouseStaff,
        label: 'Store Staff',
        hideFromMenu: true,
      },
    ],
  },
  {
    path: '/inventory',
    element: InventoryLayout,
    label: 'Inventory',
    icon: 'inventory',
    requiredDocTypes: [{ doctype: 'Stock Entry', action: 'read/create' }],
    pageChildren: [
      { path: '/inventory/stock-summary', label: 'Stock List', icon: 'list' },
      { path: '/inventory/low-stock', label: 'Low Stock', icon: 'warning' },
      { path: '/inventory/stock-ledger', label: 'Stock Ledger', icon: 'book' },
      { path: '/inventory/counter-check', label: 'Counter Stock Check', icon: 'edit' },
      { path: '/inventory/stock-snapshot', label: 'Stock Snapshot', icon: 'assessment' },
      { path: '/inventory/stock-entries', label: 'Entries', icon: 'listalt' },
      // Material Receipts hidden from the sidebar (module disabled for now)
      // { path: '/inventory/material-receipts', label: 'Material Receipts', icon: 'receipt' },
      { path: '/inventory/material-issues', label: 'Material Issues', icon: 'outbox' },
      { path: '/inventory/stock-adjustments', label: 'Stock Adjustments', icon: 'tune' },
    ],
    children: [
      {
        path: '/inventory',
        element: StockSummary,
        label: 'Stock List',
        icon: 'list',
        hideFromMenu: true,
      },
      {
        path: '/inventory/stock-summary',
        element: StockSummary,
        label: 'Stock List',
        icon: 'list',
      },
      {
        path: '/inventory/low-stock',
        element: LowStockAlert,
        label: 'Low Stock',
        icon: 'warning',
      },
      {
        path: '/inventory/stock-ledger',
        element: StockLedger,
        label: 'Stock Ledger',
        icon: 'book',
      },
      {
        path: '/inventory/counter-check',
        element: CounterStockCheck,
        label: 'Counter Stock Check',
        icon: 'edit',
      },
      {
        path: '/inventory/stock-snapshot',
        element: StockSnapshot,
        label: 'Stock Snapshot',
        icon: 'assessment',
      },
      {
        path: '/inventory/stock-entries',
        element: StockEntryList,
        label: 'Entries',
        icon: 'listalt',
      },
      {
        path: '/inventory/material-receipts',
        element: MaterialReceiptsList,
        label: 'Material Receipts',
        icon: 'receipt',
        // Hidden from the sidebar (module disabled for now); route kept for deep links
        hideFromMenu: true,
      },
      {
        path: '/inventory/material-issues',
        element: MaterialIssuesList,
        label: 'Material Issues',
        icon: 'outbox',
      },
      {
        path: '/inventory/material-transfers',
        element: MaterialTransfersList,
        label: 'Material Transfers',
        icon: 'stockTransfers',
        hideFromMenu: true,
      },
      {
        path: '/inventory/stock-adjustments',
        element: StockAdjustmentLog,
        label: 'Stock Adjustments',
        icon: 'tune',
      },
      {
        path: '/inventory/stock-entries/:stockEntryName',
        element: StockEntryDetails,
        label: 'Stock Entry Details',
        hideFromMenu: true,
      },
      {
        path: '/inventory/item-details',
        element: InventoryItemDetails,
        label: 'Item Details',
        hideFromMenu: true,
      },
      {
        path: '/inventory/material-receipt',
        element: MaterialReceipt,
        label: 'New Material Receipt',
        hideFromMenu: true,
      },
      {
        path: '/inventory/material-issue',
        element: MaterialIssue,
        label: 'New Material Issue',
        hideFromMenu: true,
      },
      {
        path: '/inventory/material-transfer',
        element: MaterialTransfer,
        label: 'New Material Transfer',
        hideFromMenu: true,
      },
      {
        path: '/inventory/stock-entry',
        element: StockEntry,
        label: 'New Stock Entry',
        hideFromMenu: true,
      },
      {
        path: '/inventory/stock-reconciliation',
        element: StockReconciliation,
        label: 'Stock Reconciliation',
        hideFromMenu: true,
      },
      {
        path: '/inventory/multi-level-reconciliation/new',
        element: CreateMultiLevelReconciliation,
        label: 'Create Multi-Level Reconciliation',
        hideFromMenu: true,
      },
      {
        path: '/inventory/multi-level-reconciliation/:id',
        element: MultiLevelReconciliationDetails,
        label: 'Reconciliation Details',
        hideFromMenu: true,
      },
      {
        path: '/inventory/multi-level-reconciliation/:id/stock-take',
        element: StockTake,
        label: 'Add Stock Take',
        hideFromMenu: true,
      },
    ],
  },
  {
    path: '/customers',
    element: Customers,
    label: 'Customers',
    icon: 'customers',
    requiredDocTypes: [{ doctype: 'Customer', action: 'read' }],
    // Page-specific child navigation
    pageChildren: [
      { path: '/customers', label: 'All Customers', icon: 'people' },
      { path: '/customers/credit', label: 'Customer Credit', icon: 'creditcard' },
      { path: '/customers/report', label: 'Customer Report', icon: 'assessment' },
    ],
    children: [
      {
        path: '/customers',
        element: Customers,
        label: 'All Customers',
        icon: 'customers',
      },
      {
        path: '/customers/credit',
        element: CustomerCredit,
        label: 'Customer Credit',
        icon: 'creditcard',
      },
      {
        path: '/customers/report',
        element: CustomerReport,
        label: 'Customer Report',
        icon: 'assessment',
        hideFromMenu: true,
      },
    ],
  },
  {
    path: '/suppliers',
    element: Suppliers,
    label: 'Suppliers',
    icon: 'suppliers',
    requiredDocTypes: [{ doctype: 'Supplier', action: 'read' }],
    // Page-specific child navigation
    pageChildren: [
      { path: '/suppliers', label: 'All Suppliers', icon: 'business' },
      { path: '/suppliers/groups', label: 'Supplier Groups', icon: 'group' },
      { path: '/suppliers/report', label: 'Supplier Report', icon: 'assessment' },
    ],
    children: [
      {
        path: '/suppliers',
        element: Suppliers,
        label: 'All Suppliers',
        icon: 'suppliers',
      },
      {
        path: '/suppliers/groups',
        element: SupplierGroups,
        label: 'Supplier Groups',
        icon: 'group',
      },
      // Dynamic routes (not in sidebar, accessed via links)
      {
        path: '/suppliers/:id',
        element: SupplierDetails,
        label: 'Supplier Details',
        hideFromMenu: true,
      },
      {
        path: '/suppliers/report',
        element: SupplierReport,
        label: 'Supplier Report',
        icon: 'assessment',
        hideFromMenu: true,
      },
    ],
  },
  {
    path: '/purchases',
    element: Purchases,
    label: 'Purchases',
    icon: 'sales',
    requiredDocTypes: [{ doctype: 'Purchase Invoice', action: 'read/create' }],
    // Page-specific child navigation
    pageChildren: [
      { path: '/purchases', label: 'All Purchases', icon: 'shoppingcart' },
      { path: '/purchases/restaurant-po', label: 'Restaurant PO', icon: 'restaurant' },
      { path: '/purchases/grns', label: 'GRN List', icon: 'receipt' },
      { path: '/purchases/invoices', label: 'Purchase Invoices', icon: 'receipt' },
      { path: '/purchases/returns', label: 'Purchase Returns', icon: 'assignmentreturn' },
      { path: '/purchases/expenses', label: 'Expenses', icon: 'money' },
    ],
    children: [
      {
        path: '/purchases',
        element: Purchases,
        label: 'All Purchases',
        icon: 'sales',
      },
      {
        path: '/purchases/new',
        element: NewPurchase,
        label: 'New Purchase',
        icon: 'add',
        hideFromMenu: true,
      },
      {
        path: '/purchases/create-order',
        element: CreatePurchaseOrder,
        label: 'Create Purchase Order',
        icon: 'add',
        hideFromMenu: true,
      },
      {
        path: '/purchases/submit-order',
        element: SubmitPurchaseOrder,
        label: 'Submit Purchase Order',
        icon: 'send',
        hideFromMenu: true,
      },
      {
        path: '/purchases/restaurant-po',
        element: RestaurantPO,
        label: 'Restaurant PO',
        icon: 'restaurant',
      },
      {
        path: '/purchases/grns',
        element: GRNList,
        label: 'GRN List',
        icon: 'receipt',
      },
      {
        path: '/purchases/returns',
        element: PurchaseReturns,
        label: 'Purchase Returns',
        icon: 'staff',
      },
      {
        path: '/purchases/returns/:id',
        element: PurchaseReturnDetails,
        label: 'Purchase Return Details',
        hideFromMenu: true,
      },
      {
        path: '/purchases/receipts',
        element: PurchaseReceiptsList,
        label: 'Purchase Receipts',
        icon: 'receipt',
        hideFromMenu: true
      },
      {
        path: '/purchases/receipts/new',
        element: NewPurchaseReceipt,
        label: 'New Purchase Receipt',
        icon: 'add',
        hideFromMenu: true,
      },
      {
        path: '/purchases/invoices',
        element: PurchaseInvoicesList,
        label: 'Purchase Invoices',
        icon: 'receipt',
      },
      {
        path: '/purchases/expenses',
        element: Expenses,
        label: 'Expenses',
        icon: 'money',
      },
      // Dynamic routes (not in sidebar, accessed via links)
      {
        path: '/purchases/:id',
        element: PurchaseDetails,
        label: 'Purchase Details',
        hideFromMenu: true,
      },
      {
        path: '/purchases/receipts/:id',
        element: PurchaseReceiptDetails,
        label: 'Purchase Receipt Details',
        hideFromMenu: true,
      },
      {
        path: '/purchases/grns/:id',
        element: GRNDetails,
        label: 'GRN Details',
        hideFromMenu: true,
      },
      {
        path: '/purchases/invoices/:invoiceNo',
        element: PurchaseInvoiceDetails,
        label: 'Purchase Invoice Details',
        hideFromMenu: true,
      },
    ],
  },
  {
    path: '/stock-transfers',
    element: StockTransfers,
    label: 'Stock Transfers',
    icon: 'swap',
    requiredDocTypes: [{ doctype: 'Material Request', action: 'read/create' }],
    children: [
      {
        // Index route — `/stock-transfers` redirects to the list. Hidden from
        // the menu so it doesn't appear as a duplicate of "All Transfers".
        path: '/stock-transfers',
        element: StockTransfers,
        label: 'Stock Transfers',
        icon: 'swap',
        hideFromMenu: true,
      },
      {
        path: '/stock-transfers/list',
        element: StockTransfersList,
        label: 'All Transfers',
        icon: 'list',
      },
      {
        path: '/stock-transfers/create-request',
        element: CreateMaterialRequest,
        label: 'Create Transfer Request',
        icon: 'add',
        hideFromMenu: true,
      },
      // Dynamic routes (not in sidebar, accessed via links)
      {
        path: '/stock-transfers/:id',
        element: StockTransferDetails,
        label: 'Transfer Details',
        hideFromMenu: true,
      },
      {
        path: '/stock-transfers/:id/approve',
        element: ApproveTransferRequest,
        label: 'Approve Transfer',
        hideFromMenu: true,
      },
      {
        path: '/stock-transfers/:id/dispatch',
        element: DispatchStock,
        label: 'Dispatch Stock',
        hideFromMenu: true,
      },
      {
        path: '/stock-transfers/:id/receive',
        element: ReceiveStock,
        label: 'Receive Stock',
        hideFromMenu: true,
      },
    ],
  },
  {
    path: '/roles',
    element: Roles,
    label: 'Roles',
    icon: 'verified',
    children: [
      {
        path: '/roles/new',
        element: RoleForm,
        label: 'Create Role',
        icon: 'add',
        hideFromMenu: true,
      },
      // Dynamic routes (not in sidebar, accessed via links)
      {
        path: '/roles/:roleName',
        element: RoleDetails,
        label: 'Role Details',
        hideFromMenu: true,
      },
      {
        path: '/roles/:roleName/edit',
        element: RoleForm,
        label: 'Edit Role',
        hideFromMenu: true,
      },
      {
        path: '/roles/:roleName/permissions',
        element: RolePermissions,
        label: 'Role Permissions',
        hideFromMenu: true,
      },
      {
        path: '/roles/:roleName/module-permissions',
        element: ModulePermissionsPage,
        label: 'Module Permissions',
        hideFromMenu: true,
      },
    ],
  },
  // {
  //   path: '/reports',
  //   element: Reports,
  //   label: 'Reports',
  //   icon: 'assessment',
  // },
  {
    path: '/settings',
    element: Settings,
    label: 'Settings',
    icon: 'settings',
    // Page-specific child navigation
    pageChildren: [
      { path: '/settings/business', label: 'Business Settings', icon: 'business' },
      { path: '/settings/pos-profile', label: 'POS Profile', icon: 'pointofsale' },
      { path: '/settings/bank-accounts', label: 'Bank Accounts', icon: 'accountbalance' },
      { path: '/settings/account-provisioning', label: 'Account Provisioning', icon: 'accountbalance' },
      { path: '/settings/payment-methods', label: 'Payment Methods', icon: 'creditcard' },
      { path: '/settings/inventory-discounts', label: 'Inventory Discounts', icon: 'localoffer' },
      { path: '/settings/loyalty-programs', label: 'Loyalty Programs', icon: 'verified' },
      { path: '/settings/module-visibility', label: 'Module Visibility', icon: 'visibility' },
      { path: '/settings/till-numbers', label: 'Till Numbers', icon: 'pointofsale' },
      { path: '/settings/database-backup', label: 'Database Backup', icon: 'download' },
    ],
    children: [
      {
        path: '/settings/business',
        element: BusinessSettings,
        label: 'Business Settings',
        icon: 'business',
      },
      {
        path: '/settings/pos-profile',
        element: POSProfileSettings,
        label: 'POS Profile',
        icon: 'sales',
      },
      {
        path: '/settings/bank-accounts',
        element: BankAccounts,
        label: 'Bank Accounts',
        icon: 'AccountBalance',
      },
      {
        path: '/settings/account-provisioning',
        element: AccountProvisioning,
        label: 'Account Provisioning',
        icon: 'AccountBalance',
      },
      {
        path: '/settings/payment-methods',
        element: PaymentMethods,
        label: 'Payment Methods',
        icon: 'CreditCard',
      },
      {
        path: '/settings/inventory-discounts',
        element: InventoryDiscounts,
        label: 'Inventory Discounts',
        icon: 'LocalOffer',
      },
      {
        path: '/settings/loyalty-programs',
        element: LoyaltyPrograms,
        label: 'Loyalty Programs',
        icon: 'table',
      },
      {
        path: '/settings/module-visibility',
        element: ModuleVisibility,
        label: 'Module Visibility',
        icon: 'visibility',
      },
      {
        path: '/settings/till-numbers',
        element: TillNumbers,
        label: 'Till Numbers',
        icon: 'pointofsale',
      },
      {
        path: '/settings/database-backup',
        element: DatabaseBackup,
        label: 'Database Backup',
        icon: 'download',
      },
      // Dynamic routes (not in sidebar, accessed via links)
      {
        path: '/settings/inventory-discounts/new',
        element: DiscountRuleForm,
        label: 'New Discount Rule',
        hideFromMenu: true,
      },
      {
        path: '/settings/inventory-discounts/:id/edit',
        element: DiscountRuleForm,
        label: 'Edit Discount Rule',
        hideFromMenu: true,
      },
    ],
  },
  {
    path: '/cashier',
    element: Cashier,
    label: 'Cashier',
    icon: 'cashier',
    pageChildren: [
      { path: '/cashier', label: 'Overview', icon: 'Dashboard' },
      { path: '/cashier/payment-method-report', label: 'Payment Method Report', icon: 'CreditCard' },
      { path: '/cashier/mpesa-notifications', label: 'Mpesa Notifications', icon: 'Notifications' },
      { path: '/cashier/submitted-payments', label: 'Submitted Payments', icon: 'Receipt' },
      { path: '/cashier/total-sales-per-person', label: 'Total Sales Per Person', icon: 'People' },
      { path: '/cashier/total-sales-per-warehouse', label: 'Total Sales Per Warehouse', icon: 'Store' },
      { path: '/cashier/item-sales-report', label: 'Item Sales Report', icon: 'Inventory' },
      { path: '/cashier/sales-detail-report', label: 'Sales Detail Report', icon: 'Receipt' },
    ],
    children: [
      {
        path: '/cashier/payment-method-report',
        element: PaymentMethodReport,
        label: 'Payment Method Report',
        icon: 'creditcard',
      },
      {
        path: '/cashier/mpesa-notifications',
        element: MpesaNotifications,
        label: 'Mpesa Notifications',
        icon: 'notifications',
      },
      {
        path: '/cashier/submitted-payments',
        element: SubmittedPayments,
        label: 'Submitted Payments',
        icon: 'receipt',
      },
      {
        path: '/cashier/total-sales-per-person',
        element: TotalSalesPerPerson,
        label: 'Total Sales Per Person',
        icon: 'staff',
      },
      {
        path: '/cashier/total-sales-per-warehouse',
        element: TotalSalesPerWarehouse,
        label: 'Total Sales Per Warehouse',
        icon: 'stores',
      },
      {
        path: '/cashier/item-sales-report',
        element: ItemSalesReport,
        label: 'Item Sales Report',
        icon: 'inventory',
      },
      {
        path: '/cashier/sales-detail-report',
        element: SalesDetailReport,
        label: 'Sales Detail Report',
        icon: 'receipt',
      },
    ],
  },
  {
    path: '/reports',
    element: Reports,
    label: 'Reports',
    icon: 'reports',
    // Page-specific child navigation
    pageChildren: [
      { path: '/reports', label: 'Overview', icon: 'Dashboard' },
      { path: '/reports/accounts', label: 'Accounts', icon: 'AccountBalance' },
      { path: '/reports/sales-analytics/report', label: 'Sales Analytics', icon: 'Analytics' },
      { path: '/reports/sales-performance', label: 'Sales Performance', icon: 'Timeline' },
      { path: '/reports/waiter-revenue', label: 'Waiter Revenue', icon: 'People' },
      { path: '/reports/inventory-valuation', label: 'Inventory Valuation', icon: 'Payments' },

      { path: '/reports/inventory-summary', label: 'Stock Summary', icon: 'Summarize' },
      { path: '/reports/aging-stock', label: 'Aging Stock', icon: 'History' },
      { path: '/reports/performance-metrics', label: 'Performance', icon: 'Timeline' },
    ],
    children: [
      {
        path: '/reports/accounts',
        element: AccountsReports,
        label: 'Accounts Reports',
        icon: 'accountbalance',
        children: [
          {
            path: '/reports/accounts/z-report',
            element: ZReport,
            label: 'Z Report',
            icon: 'receipt',
          },
          {
            path: '/reports/accounts/profit-and-loss',
            element: ProfitAndLoss,
            label: 'Profit & Loss',
            icon: 'trendingup',
          },
          {
            path: '/reports/accounts/customer-statement',
            element: CustomerStatement,
            label: 'Customer Statement',
            icon: 'customers',
          },
          {
            path: '/reports/accounts/customer-statement-detail',
            element: CustomerStatementDetail,
            label: 'Customer Statement (Detail)',
            icon: 'customers',
          },
        ],
      },

      {
        path: '/reports/sales-analytics/report',
        element: SalesAnalyticsCharts,
        label: 'Sales Analytics (Charts)',
        icon: 'reports',
      },
      {
        path: '/reports/sales-analytics/table',
        element: SalesAnalyticsTable,
        label: 'Sales Analytics (Table)',
        icon: 'listalt',
      },
      {
        path: '/reports/sales-analytics/product-analytics',
        element: ProductSalesAnalytics,
        label: 'Product Sales Analytics',
        icon: 'assessment',
      },

      {
        path: '/reports/sales-performance',
        element: SalesPerformance,
        label: 'Sales Performance',
        icon: 'reports',
      },

      {
        path: '/reports/waiter-revenue',
        element: WaiterRevenue,
        label: 'Waiter Revenue',
        icon: 'people',
      },

      {
        path: '/reports/inventory-summary',
        element: InventorySummary,
        label: 'Inventory Summary Report',
        icon: 'listalt',
      },
      {
        path: '/reports/inventory-valuation',
        element: InventoryValuation,
        label: 'Inventory Valuation',
        icon: 'money',
        pageChildren: [
          { path: '/reports/inventory-valuation/value-by-category', label: 'By Category', icon: 'ListAlt' },
          { path: '/reports/inventory-valuation/aggregated-value', label: 'Aggregated Asset Value', icon: 'Payments' },
          { path: '/reports/inventory-valuation/cost-method-comparison', label: 'Cost Method', icon: 'Compare' },
          { path: '/reports/inventory-valuation/value-trends', label: 'Value Trends', icon: 'TrendingUp' },
        ],
        children: [
          {
            path: '/reports/inventory-valuation/value-by-category',
            element: ValueByCategory,
            label: 'Value by Category',
            icon: 'listalt',
          },
          {
            path: '/reports/inventory-valuation/aggregated-value',
            element: InventoryValue,
            label: 'Aggregated Asset Value',
            icon: 'money',
          },
          {
            path: '/reports/inventory-valuation/cost-method-comparison',
            element: CostMethodComparison,
            label: 'Cost Method Comparison',
            icon: 'swap',
          },
          {
            path: '/reports/inventory-valuation/value-trends',
            element: ValueTrends,
            label: 'Inventory Value Trends',
            icon: 'trendingup',
          },
        ],
      },
      {
        path: '/reports/aging-stock',
        element: AgingStock,
        label: 'Aging Stock',
        icon: 'history',
        pageChildren: [
          { path: '/reports/aging-stock/stock-aging', label: 'Stock Aging', icon: 'History' },
          { path: '/reports/aging-stock/obsolescence-risk', label: 'Obsolescence Risk', icon: 'Warning' },
          { path: '/reports/aging-stock/aging-recommendations', label: 'Recommendations', icon: 'Info' },
        ],
        children: [
          {
            path: '/reports/aging-stock/stock-aging',
            element: StockAging,
            label: 'Stock Aging',
            icon: 'history',
          },
          {
            path: '/reports/aging-stock/obsolescence-risk',
            element: ObsolescenceRisk,
            label: 'Obsolescence Risk',
            icon: 'warning',
          },
          {
            path: '/reports/aging-stock/aging-recommendations',
            element: AgingRecommendations,
            label: 'Aging Recommendations',
            icon: 'info',
          },
        ],
      },
      {
        path: '/reports/performance-metrics',
        element: PerformanceMetrics,
        label: 'Performance Metrics',
        icon: 'reports',
        pageChildren: [
          { path: '/reports/performance-metrics/accuracy', label: 'Accuracy', icon: 'Analytics' },
          { path: '/reports/performance-metrics/variance', label: 'Variance', icon: 'Warning' },
          { path: '/reports/performance-metrics/adjustment-trends', label: 'Adjustment Trends', icon: 'TrendingUp' },
          { path: '/reports/performance-metrics/transfer-efficiency', label: 'Transfer Efficiency', icon: 'Sync' },
        ],
        children: [
          {
            path: '/reports/performance-metrics/accuracy',
            element: AccuracyReport,
            label: 'Inventory Accuracy',
            icon: 'reports',
          },
          {
            path: '/reports/performance-metrics/variance',
            element: VarianceReport,
            label: 'Inventory Variance',
            icon: 'warning',
          },
          {
            path: '/reports/performance-metrics/adjustment-trends',
            element: AdjustmentTrends,
            label: 'Adjustment Trends',
            icon: 'reports',
          },
          {
            path: '/reports/performance-metrics/transfer-efficiency',
            element: TransferEfficiency,
            label: 'Transfer Efficiency',
            icon: 'stockTransfers',
          },
        ],
      },
      {
        path: '/reports/inventory/stock-entries',
        element: StockEntryList,
        label: 'Stock Entries',
        icon: 'listAlt',
      },
      // Keep others hidden but available
      // Hidden routes still needed for functionality
      {
        path: '/reports/inventory/stock-entries/:stockEntryName',
        element: StockEntryDetails,
        label: 'Stock Entry Details',
        hideFromMenu: true,
      },
      {
        path: '/reports/inventory/material-receipts',
        element: MaterialReceiptsList,
        label: 'Material Receipts',
        icon: 'receipt',
        hideFromMenu: true,
      },
      {
        path: '/reports/inventory/material-issues',
        element: MaterialIssuesList,
        label: 'Material Issues',
        icon: 'receipt',
        hideFromMenu: true,
      },
      {
        path: '/reports/inventory/material-transfers',
        element: MaterialTransfersList,
        label: 'Material Transfers',
        icon: 'stockTransfers',
        hideFromMenu: true,
      },
      {
        path: '/reports/inventory/stock-entry',
        element: StockEntry,
        label: 'New Stock Entry',
        icon: 'edit',
        hideFromMenu: true,
      },
      {
        path: '/reports/inventory/material-receipt',
        element: MaterialReceipt,
        label: 'New Material Receipt',
        hideFromMenu: true,
      },
      {
        path: '/reports/inventory/material-issue',
        element: MaterialIssue,
        label: 'New Material Issue',
        hideFromMenu: true,
      },
      {
        path: '/reports/inventory/material-transfer',
        element: MaterialTransfer,
        label: 'New Material Transfer',
        hideFromMenu: true,
      },
    ],
  },
  {
    path: '/staff',
    element: Staff,
    label: 'Users',
    icon: 'history',
  },
  {
    path: '/attendance',
    element: Attendance,
    label: 'Attendance',
    icon: 'accessTime',
  },
  {
    path: '/staff-performance',
    element: StaffPerformance,
    label: 'Staff Performance',
    icon: 'trendingUp',
  },
  {
    path: '/shift-management',
    element: ShiftManagement,
    label: 'Shift Management',
    icon: 'attendance',
  },
  {
    path: '/marketing',
    element: Marketing,
    label: 'Marketing',
    icon: 'campaign',
  },
  {
    path: '/crm',
    element: CRM,
    label: 'CRM',
    icon: 'customers',
    requiredDocTypes: [{ doctype: 'Customer', action: 'read' }],
    pageChildren: [
      { path: '/crm', label: 'Customer 360', icon: 'customers' },
      { path: '/crm/promotions', label: 'Promotions', icon: 'megaphone' },
      { path: '/crm/broadcasts', label: 'Broadcast History', icon: 'history' },
    ],
    children: [
      { path: '/crm', element: CRM, label: 'Customer 360', icon: 'customers' },
      { path: '/crm/promotions', element: CRMPromotions, label: 'Promotions', icon: 'megaphone' },
      { path: '/crm/broadcasts', element: CRMBroadcasts, label: 'Broadcast History', icon: 'history' },
      { path: '/crm/customers/:id', element: CRMCustomerDetail, label: 'Customer Profile', icon: 'customers', hideFromMenu: true },
    ],
  },
  {
    path: '/service-tickets',
    element: ServiceTickets,
    label: 'Service Tickets',
    icon: 'servicetickets',
  },
  {
    path: '/service-tickets/new',
    element: NewTicket,
    label: 'New Ticket',
    icon: 'servicetickets',
    hideFromMenu: true,
  },
  {
    path: '/service-tickets/:id',
    element: TicketDetails,
    label: 'Service Ticket Details',
    icon: 'servicetickets',
    hideFromMenu: true,
  },
  {
    path: '/permission-denied',
    element: PermissionDenied,
    label: 'Permission Denied',
    hideFromMenu: true,
  },
];

// All routes (for 404 handling)
export const routes = [
  ...publicRoutes,
  ...protectedRoutesWithoutLayout,
  ...protectedRoutes,
  {
    path: '*',
    element: NotFound,
    label: 'Not Found',
  },
];

// Helper function to get route by path
export const getRouteByPath = (path) => {
  return routes.find((route) => route.path === path);
};

// Helper function to get all navigation routes (excludes 404 and public routes)
export const getNavigationRoutes = () => {
  return protectedRoutes;
};

